/**
 * `useToastStore` — the toast queue backing `<ToastHost>` (frontend.md §2.1, §4.2).
 *
 * A small Zustand store rather than a React context: any hook (notably `useErrorHandler`)
 * can push a toast without threading a context through. `<ToastHost>` subscribes and
 * renders the queue.
 *
 * A toast may carry a `retry` callback (the `external`-error retry affordance — the
 * frontend never auto-retries, the user clicks) and/or an `errorReport` (the `system`-
 * error "copy error report" affordance — the full `ErrorEnvelope`, copyable for a bug
 * report).
 */

import { create } from 'zustand';
import type { ErrorEnvelope } from '@/types';

export type ToastKind = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id: string;
  kind: ToastKind;
  title: string;
  message: string;
  /** External-error retry affordance — re-issues the failed query on explicit user click. */
  retry?: (() => void) | undefined;
  /** System-error "copy error report" affordance — the full envelope, copyable. */
  errorReport?: ErrorEnvelope | undefined;
}

/** What a caller passes to `push` — the store assigns the `id`. */
export type ToastInput = Omit<Toast, 'id'>;

interface ToastState {
  toasts: Toast[];
  push: (toast: ToastInput) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

let toastSeq = 0;

/** Hard ceiling on simultaneously-queued toasts — a repeated failure (e.g. a polling query
 *  against a downed backend) must never wall the screen with notifications. Keep the newest. */
const MAX_TOASTS = 5;

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  push: (toast) => {
    // Collapse duplicates: an identical (kind + title + message) toast already on screen is
    // NOT re-added — without this, a query that keeps failing stacks the same toast over and
    // over into a "UI explosion". Return the existing id so callers still get a handle.
    const existing = get().toasts.find(
      (t) => t.kind === toast.kind && t.title === toast.title && t.message === toast.message,
    );
    if (existing) return existing.id;
    const id = `toast-${++toastSeq}`;
    // Cap the queue at the newest MAX_TOASTS so distinct-but-bursty errors can't pile up either.
    set((s) => ({ toasts: [...s.toasts, { ...toast, id }].slice(-MAX_TOASTS) }));
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
}));
